import React, { useState } from 'react';
import { Layout, Dropdown, Input } from 'antd';
import type { MenuProps } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';

const { Header } = Layout;
const { Search } = Input;

const ROUTE_TITLES: Record<string, string> = {
  '/': 'Trang chủ',
  '/admin-management': 'Quản lý admin',
  '/vouchers': 'Quản lý voucher',
  '/shop-promotions': 'KM quán (Combo & BXGY)',
  '/shop-promotions/create': 'Thêm KM quán',
  '/vouchers/create': 'Create New Voucher',
  '/vouchers/:id': 'Quản lý voucher',
  '/users': 'Quản lý người dùng',
  '/user-management': 'Quản lý người dùng',
  '/merchants': 'Quản lý quán',
  '/merchants/create': 'Thêm quán',
  '/merchants/:id/edit': 'Sửa quán',
  '/orders': 'Quản lý đơn hàng',
  '/transaction-logs': 'Transaction Logs',
  '/categories': 'Quản lý danh mục',
  '/analytics': 'Báo cáo',
  '/application-fees': 'Phí ứng dụng',
  '/settings': 'Settings',
  '/payment-methods': 'Phương thức thanh toán',
  '/dynamic-sections': 'Dynamic Sections',
  '/ads': 'Quản lý quảng cáo',
};

function getRoleLabel(role?: string): string {
  if (!role) return 'Admin';
  const r = role.toLowerCase();
  if (r === 'super_admin' || r === 'superadmin') return 'Super Admin';
  if (r === 'admin' || r === 'editor') return 'Admin';
  if (r === 'support' || r === 'viewer') return 'Support';
  return role;
}

interface TopbarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  user?: { name?: string; email?: string; role?: string };
  onLogout: () => void;
  breadcrumbs?: Array<{ path: string; label: string }>;
  showBreadcrumbs?: boolean;
  actions?: React.ReactNode;
}

export const Topbar: React.FC<TopbarProps> = ({
  collapsed,
  onToggleCollapse,
  user,
  onLogout,
  actions,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const pageTitle =
    ROUTE_TITLES[pathname] ||
    (pathname.startsWith('/user-management') ? 'User Management' : null) ||
    (pathname.startsWith('/admin-management') ? 'Admin Management' : null) ||
    (pathname.startsWith('/vouchers/create') ? 'Create New Voucher' : null) ||
    (pathname.match(/^\/vouchers\/\d+$/) ? 'Voucher Management' : null) ||
    (pathname.startsWith('/shop-promotions/create') ? 'Thêm KM quán' : null) ||
    (pathname.match(/^\/shop-promotions\/\d+\/edit$/) ? 'Sửa KM quán' : null) ||
    (pathname.match(/^\/shop-promotions\/\d+$/) ? 'Chi tiết KM quán' : null) ||
    (pathname.startsWith('/shop-promotions')
      ? 'KM quán (Combo & BXGY)'
      : null) ||
    (pathname === '/merchants/create' ? 'Add Merchant' : null) ||
    (pathname.match(/^\/merchants\/\d+\/edit$/) ? 'Edit Merchant' : null) ||
    (pathname.match(/^\/merchants\/\d+$/) ? 'Merchant Details' : null) ||
    (pathname.startsWith('/ads') ? 'Ads Management' : null) ||
    (pathname.startsWith('/transaction-logs') ? 'Transaction Logs' : null) ||
    ROUTE_TITLES['/'] ||
    'Dashboard Overview';

  const userMenuItems: MenuProps['items'] = [
    // { key: 'profile', icon: <UserOutlined />, label: 'Profile' },
    // { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: onLogout,
    },
  ];

  return (
    <Header className="admin-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          onClick={onToggleCollapse}
          role="button"
          tabIndex={0}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{ cursor: 'pointer', color: '#6b7280', fontSize: 18 }}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </span>
        <div className="admin-header-title">{pageTitle}</div>
      </div>

      <div className="admin-header-search-wrap">
        <Search
          placeholder="Tìm đơn hàng, quán, chiến dịch..."
          allowClear
          enterButton={<SearchOutlined />}
          size="middle"
          onSearch={value => {
            if (!value.trim()) return;
            const q = value.trim();
            const path = location.pathname;
            if (path.startsWith('/ads')) {
              navigate(`/ads?search=${encodeURIComponent(q)}`);
            } else if (path.startsWith('/merchants')) {
              navigate(`/merchants?search=${encodeURIComponent(q)}`);
            } else {
              navigate(`/orders?search=${encodeURIComponent(q)}`);
            }
          }}
        />
      </div>

      <div className="admin-header-right">
        {actions && <div>{actions}</div>}
        <div
          className="admin-header-bell"
          role="button"
          tabIndex={0}
          aria-label="Notifications"
        >
          <BellOutlined style={{ fontSize: 18 }} />
        </div>
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          trigger={['click']}
        >
          <div className="admin-header-user" style={{ cursor: 'pointer' }}>
            <div className="admin-header-avatar">
              <UserOutlined style={{ fontSize: 18 }} />
            </div>
            <div className="admin-header-user-text">
              <span className="admin-header-user-name">
                {user?.name || 'Admin User'}
              </span>
              <span className="admin-header-user-role">
                {getRoleLabel(user?.role)}
              </span>
            </div>
          </div>
        </Dropdown>
      </div>
    </Header>
  );
};
